import React, { useState } from 'react';
import { Square, Sidebar, Layout, Edit3, Calendar } from 'lucide-react';
import { GridAssetPanel } from './GridAssetPanel';
import { EditorModal } from './EditorModal';
import { useImageInteraction } from './hooks/useImageInteraction';
import { useCropCalculation } from './hooks/useCropCalculation';
import { useDrawing } from './hooks/useDrawing';
import { CloudinaryOverlays } from '@/utils/AspectWeaveCloudinaryOverlays';
import {
  VisualAssetEditorProps,
  AspectRatioOption,
  ViewModeOption,
  ToolbarAction,
  ImageVersion,
  ViewportData,
  EditorStateData,
  Rectangle,
  ImagePosition
} from './types';

// Default configurations
const DEFAULT_ASPECT_RATIOS: AspectRatioOption[] = [
  { value: '1:1', label: '1:1', className: 'aspect-square' },
  { value: '16:9', label: '16:9', className: 'aspect-video' },
  { value: '4:3', label: '4:3', className: 'aspect-[4/3]' },
];

const DEFAULT_VIEW_MODES: ViewModeOption[] = [
  { value: 'side_by_side', label: 'Side by Side', icon: Sidebar },
  { value: 'stacked', label: 'Stacked', icon: Layout },
  { value: 'single', label: 'Single', icon: Square },
];

const DEFAULT_TOOLBAR_ACTIONS: ToolbarAction[] = [
  {
    icon: Edit3,
    label: 'Edit Image',
    onClick: (index) => {}
  },
  {
    icon: Calendar,
    label: 'Set Date',
    onClick: (index) => {}
  }
];

export const VisualAssetEditor: React.FC<VisualAssetEditorProps> = ({
  onSave,
  onCancel,
  initialData = {},
  aspectRatios = DEFAULT_ASPECT_RATIOS,
  viewModes = DEFAULT_VIEW_MODES,
  theme = {},
  showDebug = false,
  customToolbarActions = DEFAULT_TOOLBAR_ACTIONS,
  renderImage,
  renderFooter,
  className,
  showSaveControls = false,
  onCropUrlsChange,
  onStateChange,
  initialViewportState,
  enableCollectionChange,
  initialCollectionId,
  onCollectionChange
}) => {
  // Map database comparison_mode to editor viewMode format (keep underscore format)
  const mapDatabaseToViewMode = (dbMode: string) => {
    switch (dbMode) {
      case 'side_by_side': return 'side_by_side';
      case 'stacked': return 'stacked';
      case 'single': return 'single';
      default: return 'side_by_side';
    }
  };

  // Map editor viewMode to database comparison_mode format (already same format)
  const mapViewModeToDatabase = (viewMode: string) => {
    switch (viewMode) {
      case 'side_by_side': return 'side_by_side';
      case 'stacked': return 'stacked';
      case 'single': return 'single';
      default: return 'side_by_side';
    }
  };

  // Basic state
  const [aspectRatio, setAspectRatio] = useState(initialData.aspectRatio || '1:1');
  const [viewMode, setViewMode] = useState(
    initialData.viewMode || mapDatabaseToViewMode(initialData.comparison_mode || 'side_by_side')
  );
  const [header, setHeader] = useState(() => {
    return initialData.header || '';
  });
  const [subheader, setSubheader] = useState(() => {
    return initialData.subheader || '';
  });
  const [companyName, setCompanyName] = useState(initialData.companyName || '');
  const [companyLogo, setCompanyLogo] = useState(initialData.companyLogo || '');
  
  // Versioned images state
  const [primaryImages, setPrimaryImages] = useState<ImageVersion[]>(
    initialData.primaryImages || (initialData.primaryImage ? [{ version: '20250401', url: initialData.primaryImage }] : [])
  );
  const [secondaryImages, setSecondaryImages] = useState<ImageVersion[]>(
    initialData.secondaryImages || (initialData.secondaryImage ? [{ version: '20250706', url: initialData.secondaryImage }] : [])
  );
  const [selectedPrimaryVersion, setSelectedPrimaryVersion] = useState(
    initialData.selectedPrimaryVersion || primaryImages[0]?.version || '20250401'
  );
  const [selectedSecondaryVersion, setSelectedSecondaryVersion] = useState(
    initialData.selectedSecondaryVersion || secondaryImages[0]?.version || '20250706'
  );
  
  // Legacy support - derived from versioned images
  const primaryImage = primaryImages.find(img => img.version === selectedPrimaryVersion)?.url || '';
  const secondaryImage = secondaryImages.find(img => img.version === selectedSecondaryVersion)?.url || '';
  
  const [hoveredImage, setHoveredImage] = useState<number | null>(null);
  const [cropUrls, setCropUrls] = useState<{ primary?: string; secondary?: string }>(() => {
    // Initialize from existing asset data if available
    return {
      primary: initialData.customData?.primary_cropped_url || undefined,
      secondary: initialData.customData?.secondary_cropped_url || undefined
    };
  });

  // Determine available view modes based on image availability
  const availableViewModes = React.useMemo(() => {
    const hasPrimary = primaryImages.length > 0;
    const hasSecondary = secondaryImages.length > 0;
    
    if (!hasPrimary && !hasSecondary) {
      return viewModes.filter(mode => mode.value === 'single');
    }
    
    if (hasPrimary && hasSecondary) {
      return viewModes; // All modes available
    }
    
    // Only one set of images available
    return viewModes.filter(mode => mode.value === 'single');
  }, [primaryImages, secondaryImages, viewModes]);

  // Auto-update view mode when images change
  React.useEffect(() => {
    const currentModeAvailable = availableViewModes.some(mode => mode.value === viewMode);
    if (!currentModeAvailable) {
      const newMode = availableViewModes.length > 1 ? 'side_by_side' : 'single';
      setViewMode(newMode);
    }
  }, [availableViewModes, viewMode]);

  // Add state for grid image management
  const [gridImageZooms, setGridImageZooms] = React.useState<Record<string, number>>({});
  const [gridImagePositions, setGridImagePositions] = React.useState<Record<string, { x: number; y: number }>>({});

  // Extract initial viewport state for imageInteraction hook
  const initialImageInteractionState = React.useMemo(() => {
    if (!initialViewportState) return {};

    const initialZooms: Record<number, number> = {};
    const initialPositions: Record<number, ImagePosition> = {};

    if (initialViewportState.primary) {
      initialZooms[0] = initialViewportState.primary.zoom;
      initialPositions[0] = {
        x: initialViewportState.primary.pan_x,
        y: initialViewportState.primary.pan_y
      };
    }

    if (initialViewportState.secondary) {
      initialZooms[1] = initialViewportState.secondary.zoom;
      initialPositions[1] = {
        x: initialViewportState.secondary.pan_x,
        y: initialViewportState.secondary.pan_y
      };
    }

    return { initialZooms, initialPositions };
  }, [initialViewportState]);

  // Link state for synchronized panning
  const [isLinked, setIsLinked] = useState(false);

  // Custom hooks
  const imageInteraction = useImageInteraction({
    ...initialImageInteractionState,
    isLinked,
    viewMode
  });
  const drawing = useDrawing();
  const cropCalculation = useCropCalculation(
    true, // Always enabled since component is now always "open"
    primaryImage,
    secondaryImage,
    imageInteraction.imageZooms,
    imageInteraction.imagePositions,
    gridImageZooms,
    gridImagePositions,
    drawing.drawingState.rectangles,
    viewMode,
    aspectRatio
  );

  // Enhanced mouse handling for drawing
  const [currentDrawing, setCurrentDrawing] = useState<{ startX: number; startY: number; currentX: number; currentY: number; imageIndex: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent, imageIndex: number) => {

    if (drawing.drawingState.activeTool === 'rectangle') {
      const imageElement = e.currentTarget as HTMLImageElement;
      const rect = imageElement.getBoundingClientRect();
      const containerRect = imageElement.parentElement?.getBoundingClientRect();
      
      if (containerRect) {
        // Get coordinates relative to the image container
        const clientX = e.clientX - containerRect.left;
        const clientY = e.clientY - containerRect.top;

        // Convert container coordinates to image coordinates
        // The image has transform: translate(position.x, position.y) scale(zoom)
        // The rendering formula is: clientPos = position + (imageCoord * zoom)
        // So: imageCoord = (clientPos - position) / zoom
        const imagePosition = imageInteraction.imagePositions[imageIndex] || { x: 0, y: 0 };
        const imageZoom = imageInteraction.imageZooms[imageIndex] || 1;

        const imageX = (clientX - imagePosition.x) / imageZoom;
        const imageY = (clientY - imagePosition.y) / imageZoom;


        drawing.startDrawing(imageIndex, clientX, clientY);
        setCurrentDrawing({ startX: clientX, startY: clientY, currentX: clientX, currentY: clientY, imageIndex });
      }
    } else {
      imageInteraction.handleMouseDown(e, imageIndex);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {

    if (drawing.drawingState.isDrawing && currentDrawing) {
      const imageElement = e.currentTarget as HTMLImageElement;
      const containerRect = imageElement.parentElement?.getBoundingClientRect();
      
      if (containerRect) {
        // Get coordinates relative to the image container
        const clientX = e.clientX - containerRect.left;
        const clientY = e.clientY - containerRect.top;

        // Convert container coordinates to image coordinates
        // The image has transform: translate(position.x, position.y) scale(zoom)
        // The rendering formula is: clientPos = position + (imageCoord * zoom)
        // So: imageCoord = (clientPos - position) / zoom
        const imagePosition = imageInteraction.imagePositions[currentDrawing.imageIndex] || { x: 0, y: 0 };
        const imageZoom = imageInteraction.imageZooms[currentDrawing.imageIndex] || 1;

        setCurrentDrawing(prev => prev ? { ...prev, currentX: clientX, currentY: clientY } : null);
      }
    } else {
      imageInteraction.handleMouseMove(e);
    }
  };

  const handleMouseUp = () => {
    
    if (drawing.drawingState.isDrawing && currentDrawing) {
      const imageIndex = currentDrawing.imageIndex; // Use the tracked image index

      drawing.finishDrawing(imageIndex, currentDrawing.currentX, currentDrawing.currentY, handleRectangleChange);
      setCurrentDrawing(null);
    } else {
      imageInteraction.handleMouseUp();
    }
  };

  const handleRectangleChange = (rectangles: Record<string, Array<{ x: number; y: number; width: number; height: number; color?: string }>>) => {
    
    // Store rectangles using string keys to maintain consistency
    drawing.setRectangles(rectangles);
    
    // Trigger crop calculation update
    cropCalculation.updateDebugCropParams();
  };

  // Memoize rectangle conversion to ensure DebugPanel updates when drawing state changes
  const rectanglesForDebug = React.useMemo(() => {
    const stringKeyRectangles: Record<string, Array<{ x: number; y: number; width: number; height: number; color?: string }>> = {};

    // Get the correct keys for current view mode
    const getDebugAnnotationKeys = () => {
      switch (viewMode) {
        case 'single':
          return { primary: 'primary3', secondary: 'secondary3' };
        case 'side_by_side':
          return { primary: 'primary1', secondary: 'secondary1' };
        case 'stacked':
          return { primary: 'primary2', secondary: 'secondary2' };
        default:
          return { primary: 'primary1', secondary: 'secondary1' };
      }
    };

    const debugKeys = getDebugAnnotationKeys();

    // Handle both numeric keys (0, 1) and string keys (primary1, primary3, etc.)
    Object.entries(drawing.drawingState.rectangles).forEach(([key, rects]) => {
      let stringKey: string;

      // Check if key is numeric
      const numKey = parseInt(key);
      if (!isNaN(numKey)) {
        // Numeric keys (0, 1) - convert to view-mode-specific string keys
        stringKey = numKey === 0 ? debugKeys.primary : debugKeys.secondary;
      } else {
        // Already string keys - use as-is
        stringKey = key;
      }

      stringKeyRectangles[stringKey] = rects;
    });
    return stringKeyRectangles;
  }, [drawing.drawingState.rectangles, viewMode]);

  // Convert rectangles to numeric indexes for AssetCard component
  const rectanglesForAssetCard = React.useMemo(() => {
    // console.log('🚨 RECTANGLES FOR ASSET CARD CONVERSION STARTED 🚨');
    const numericRectangles: Record<number, Rectangle[]> = {};

    // Get the correct keys for current view mode
    const getDebugAnnotationKeys = () => {
      switch (viewMode) {
        case 'single':
          return { primary: 'primary3', secondary: 'secondary3' };
        case 'side_by_side':
          return { primary: 'primary1', secondary: 'secondary1' };
        case 'stacked':
          return { primary: 'primary2', secondary: 'secondary2' };
        default:
          return { primary: 'primary1', secondary: 'secondary1' };
      }
    };

    const debugKeys = getDebugAnnotationKeys();

    // Convert drawing state rectangles to numeric keys for AssetCard
    Object.entries(drawing.drawingState.rectangles).forEach(([key, rects]) => {
      let numericIndex: number | null = null;

      // Check if key is already numeric
      const numKey = parseInt(key);
      if (!isNaN(numKey)) {
        // Use numeric key as-is
        numericIndex = numKey;
      } else {
        // Convert string keys to numeric indexes based on view mode
        if (key === debugKeys.primary) {
          numericIndex = 0; // Primary image
        } else if (key === debugKeys.secondary) {
          numericIndex = 1; // Secondary image
        }
      }

      // Only add if we found a valid numeric index
      if (numericIndex !== null) {
        numericRectangles[numericIndex] = rects;
      }
    });

    return numericRectangles;
  }, [drawing.drawingState.rectangles, viewMode]);

  // Initialize crop URLs on component mount and load initial state
  React.useEffect(() => {
    
    // Create distinct paths for initialization
    if (initialViewportState) {
      // PATH 1: Custom initial state provided - DON'T clear, just apply
      
      // Load header and subheader immediately
      if (initialViewportState.header !== undefined) {
        setHeader(initialViewportState.header);
      }
      if (initialViewportState.subheader !== undefined) {
        setSubheader(initialViewportState.subheader);
      }
      
      // Use a timeout to ensure all components are mounted for viewport changes
      const initTimeout = setTimeout(() => {
        let allRectangles: Record<string, Array<{ x: number; y: number; width: number; height: number; color?: string }>> = {};
        
        // Get the correct annotation keys based on current view mode
        const getLoadAnnotationKeys = () => {
          switch (viewMode) {
            case 'single':
              return { primary: 'primary3', secondary: 'secondary3' };
            case 'side_by_side':
              return { primary: 'primary1', secondary: 'secondary1' };
            case 'stacked':
              return { primary: 'primary2', secondary: 'secondary2' };
            default:
              return { primary: 'primary1', secondary: 'secondary1' };
          }
        };

        const loadAnnotationKeys = getLoadAnnotationKeys();

        // Load zoom and pan positions for primary image
        if (initialViewportState.primary) {
          const primaryState = initialViewportState.primary;

           // Set zoom levels for grid
           setGridImageZooms(prev => {
             const newZooms = { ...prev, [loadAnnotationKeys.primary]: primaryState.zoom };
             return newZooms;
           });

           // Also set for imageInteraction (index 0 = primary)
           imageInteraction.handleSetZoom(0, Math.round(primaryState.zoom * 100), true); // Skip position reset

           // FORCE UPDATE: Ensure both systems are synchronized
           setTimeout(() => {
             imageInteraction.handleSetZoom(0, Math.round(primaryState.zoom * 100), true);
           }, 100);

           // Set pan positions for grid
           setGridImagePositions(prev => {
             const newPositions = { ...prev, [loadAnnotationKeys.primary]: { x: primaryState.pan_x, y: primaryState.pan_y } };
             return newPositions;
           });

           // Also set for imageInteraction (index 0 = primary)
           imageInteraction.handleSetPosition(0, primaryState.pan_x, primaryState.pan_y);

           // FORCE UPDATE: Ensure both systems are synchronized
           setTimeout(() => {
             imageInteraction.handleSetPosition(0, primaryState.pan_x, primaryState.pan_y);
           }, 100);

          // Collect annotations/rectangles with view-mode-specific keys
          if (primaryState.annotations && primaryState.annotations.length > 0) {
            allRectangles[loadAnnotationKeys.primary] = primaryState.annotations.map(annotation => ({
              x: annotation.x,
              y: annotation.y,
              width: annotation.width,
              height: annotation.height,
              color: annotation.color || '#84cc16'
            }));
          }
        }
        
        // Load zoom and pan positions for secondary image
        if (initialViewportState.secondary) {
          const secondaryState = initialViewportState.secondary;

           // Set zoom levels for grid
           setGridImageZooms(prev => {
             const newZooms = { ...prev, [loadAnnotationKeys.secondary]: secondaryState.zoom };
             return newZooms;
           });

           // Also set for imageInteraction (index 1 = secondary)
           imageInteraction.handleSetZoom(1, Math.round(secondaryState.zoom * 100), true); // Skip position reset

           // FORCE UPDATE: Ensure both systems are synchronized
           setTimeout(() => {
             imageInteraction.handleSetZoom(1, Math.round(secondaryState.zoom * 100), true);
           }, 100);

           // Set pan positions for grid
           setGridImagePositions(prev => {
             const newPositions = { ...prev, [loadAnnotationKeys.secondary]: { x: secondaryState.pan_x, y: secondaryState.pan_y } };
             return newPositions;
           });

           // Also set for imageInteraction (index 1 = secondary)
           imageInteraction.handleSetPosition(1, secondaryState.pan_x, secondaryState.pan_y);

           // FORCE UPDATE: Ensure both systems are synchronized
           setTimeout(() => {
             imageInteraction.handleSetPosition(1, secondaryState.pan_x, secondaryState.pan_y);
           }, 100);

          // Collect annotations/rectangles with view-mode-specific keys
          if (secondaryState.annotations && secondaryState.annotations.length > 0) {
            allRectangles[loadAnnotationKeys.secondary] = secondaryState.annotations.map(annotation => ({
              x: annotation.x,
              y: annotation.y,
              width: annotation.width,
              height: annotation.height,
              color: annotation.color || '#ef4444'
            }));
          }
        }
        
        // Set all rectangles at once - keep string keys for drawing state
        if (Object.keys(allRectangles).length > 0) {
          drawing.setRectangles(allRectangles);
        }
        
        // Mark initialization as complete after all state has been applied
        setTimeout(() => {
          setIsInitializing(false);
        }, 100);
      }, 500); // Increased delay to ensure DOM is ready
      
    } else {
      // PATH 2: Normal initialization - clear previous state and set defaults
      setCropUrls({ primary: undefined, secondary: undefined });
      drawing.setRectangles({}); // Clear any previous rectangles
      
      // Reset to default zoom and positions
      setGridImageZooms({});
      setGridImagePositions({});
      setIsInitializing(false); // Not initializing from custom state
    }
    
    // Generate fresh crop URLs without overlays after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(async () => {
      const croppedUrls = await cropCalculation.generateCroppedUrls();
      
      // Don't apply overlays on initial load - just use clean cropped URLs
      const urls = {
        primary: croppedUrls.primaryCroppedUrl || undefined,
        secondary: croppedUrls.secondaryCroppedUrl || undefined
      };
      setCropUrls(urls);
      if (onCropUrlsChange) {
        onCropUrlsChange(urls);
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, []); // Remove initialViewportState from dependencies to prevent re-runs

  // Auto-update crop URLs when interactions change
  React.useEffect(() => {
    const updateCropUrls = async () => {
      const croppedUrls = await cropCalculation.generateCroppedUrls();
      const primaryOverlayUrl = generateUrlWithOverlays(croppedUrls.primaryCroppedUrl, 0);
      const secondaryOverlayUrl = generateUrlWithOverlays(croppedUrls.secondaryCroppedUrl, 1);
      
      const urls = {
        primary: primaryOverlayUrl || undefined,
        secondary: secondaryOverlayUrl || undefined
      };
      setCropUrls(urls);
      if (onCropUrlsChange) {
        onCropUrlsChange(urls);
      }
    };
    
    // Debounce the updates to avoid too many calls during interactions
    const timeoutId = setTimeout(updateCropUrls, 300);
    return () => clearTimeout(timeoutId);
  }, [
    imageInteraction.imageZooms,
    imageInteraction.imagePositions,
    gridImageZooms,
    gridImagePositions,
    drawing.drawingState.rectangles,
    primaryImage,
    secondaryImage,
    viewMode,
    aspectRatio
  ]);


  // Track initialization to prevent premature state callbacks
  const [isInitializing, setIsInitializing] = useState(!!initialViewportState);

  // Live state change callback with ref to prevent infinite loops
  const onStateChangeRef = React.useRef(onStateChange);
  React.useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  const stateDataRef = React.useRef<EditorStateData | null>(null);
  React.useEffect(() => {
    // Don't call state change during initialization
    if (onStateChangeRef.current && !isInitializing) {
      const formatViewportData = (
        zoom: Record<string, number>,
        positions: Record<string, { x: number; y: number }>,
        positionKey: string,
        annotationKey: string
      ): ViewportData => {
        const annotations = rectanglesForDebug[annotationKey] || [];

        return {
          zoom: zoom[positionKey] || 1,
          pan_x: positions[positionKey]?.x || 0,
          pan_y: positions[positionKey]?.y || 0,
          annotations
        };
      };

      // Map view mode to correct annotation keys
      const getAnnotationKeys = () => {
        switch (viewMode) {
          case 'single':
            return { primary: 'primary3', secondary: 'secondary3' };
          case 'side_by_side':
            return { primary: 'primary1', secondary: 'secondary1' };
          case 'stacked':
            return { primary: 'primary2', secondary: 'secondary2' };
          default:
            return { primary: 'primary1', secondary: 'secondary1' };
        }
      };

      const annotationKeys = getAnnotationKeys();

      const stateData: EditorStateData = {
        header,
        subheader,
        viewMode, // Include current view mode for debug panel
        aspectRatio, // Include current aspect ratio for saving
        primary: formatViewportData(
          { ...imageInteraction.imageZooms, ...gridImageZooms },
          { ...imageInteraction.imagePositions, ...gridImagePositions },
          '0',
          annotationKeys.primary
        ),
        secondary: formatViewportData(
          { ...imageInteraction.imageZooms, ...gridImageZooms },
          { ...imageInteraction.imagePositions, ...gridImagePositions },
          '1',
          annotationKeys.secondary
        ),
        croppedUrls: {
          primaryCroppedUrl: cropUrls.primary,
          secondaryCroppedUrl: cropUrls.secondary
        }
      };

      // Only call onStateChange if the data actually changed
      const stateDataString = JSON.stringify(stateData);
      const previousStateString = JSON.stringify(stateDataRef.current);

      if (stateDataString !== previousStateString) {
        stateDataRef.current = stateData;
        onStateChangeRef.current(stateData);
      }
    } else {
    }
  }, [
    header,
    subheader,
    viewMode, // Add viewMode to dependencies to trigger debug panel updates
    aspectRatio, // Add aspectRatio to dependencies to trigger updates when aspect ratio changes
    imageInteraction.imageZooms,
    imageInteraction.imagePositions,
    gridImageZooms,
    gridImagePositions,
    rectanglesForDebug,
    drawing.drawingState.rectangles, // Add drawing rectangles to ensure annotations trigger updates
    cropUrls, // Add cropUrls to trigger state change when Save generates new URLs
    isInitializing  // Add to dependencies so it re-runs when initialization completes
  ]);

  // Track if we've initialized to avoid overriding user changes
  const [hasInitializedViewMode, setHasInitializedViewMode] = React.useState(false);
  const [hasInitializedAspectRatio, setHasInitializedAspectRatio] = React.useState(false);

  // Update state when initialData changes
  React.useEffect(() => {
    if (initialData) {
      // Only set aspect ratio on first initialization, not on subsequent data updates
      if (!hasInitializedAspectRatio && initialData.aspectRatio !== undefined) {
        setAspectRatio(initialData.aspectRatio);
        setHasInitializedAspectRatio(true);
      }

      // Only set view mode on first initialization, not on subsequent data updates
      if (!hasInitializedViewMode) {
        if (initialData.viewMode !== undefined) {
          setViewMode(initialData.viewMode);
        } else if (initialData.comparison_mode !== undefined) {
          setViewMode(mapDatabaseToViewMode(initialData.comparison_mode));
        }
        setHasInitializedViewMode(true);
      }
      
      // Only set header/subheader from initialData if we don't have initialViewportState
      if (!initialViewportState) {
        if (initialData.header !== undefined) setHeader(initialData.header);
        if (initialData.subheader !== undefined) setSubheader(initialData.subheader);
      }
      
      if (initialData.companyName !== undefined) setCompanyName(initialData.companyName);
      if (initialData.companyLogo !== undefined) setCompanyLogo(initialData.companyLogo);
      
      // Handle versioned images
      if (initialData.primaryImages !== undefined) {
        setPrimaryImages(initialData.primaryImages);
        if (initialData.selectedPrimaryVersion) {
          setSelectedPrimaryVersion(initialData.selectedPrimaryVersion);
        }
      } else if (initialData.primaryImage !== undefined) {
        setPrimaryImages([{ version: '20250401', url: initialData.primaryImage }]);
      }
      
      if (initialData.secondaryImages !== undefined) {
        setSecondaryImages(initialData.secondaryImages);
        if (initialData.selectedSecondaryVersion) {
          setSelectedSecondaryVersion(initialData.selectedSecondaryVersion);
        }
      } else if (initialData.secondaryImage !== undefined) {
        setSecondaryImages([{ version: '20250706', url: initialData.secondaryImage }]);
      }
    }
  }, [initialData]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => {
      imageInteraction.setActiveDropdown(null);
    };
    
    if (imageInteraction.activeDropdown !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [imageInteraction.activeDropdown, imageInteraction.setActiveDropdown]);

  // Initialize rectangles from saved data
  React.useEffect(() => {
    if (initialData.customData?.rectangles) {
      drawing.setRectangles(initialData.customData.rectangles);
    }
  }, [initialData.customData?.rectangles, drawing.setRectangles]);

  const handleSave = async (collectionId?: string) => {
    // Generate cropped URLs before saving
    const croppedUrls = await cropCalculation.generateCroppedUrls();


    // Update internal state and call callback
    const urls = {
      primary: croppedUrls.primaryCroppedUrl || undefined,
      secondary: croppedUrls.secondaryCroppedUrl || undefined
    };
    setCropUrls(urls);
    if (onCropUrlsChange) {
      onCropUrlsChange(urls);
    }

    onSave({
      aspectRatio,
      viewMode,
      comparison_mode: mapViewModeToDatabase(viewMode), // Add database format
      header,
      subheader,
      companyName,
      primaryImages,
      secondaryImages,
      selectedPrimaryVersion,
      selectedSecondaryVersion,
      // Legacy support
      primaryImage,
      secondaryImage,
      customData: {
        ...initialData.customData,
        croppedUrls,
        rectangles: drawing.drawingState.rectangles
      }
    }, collectionId);
  };

  // Enhanced cropped URL generation with overlays
  const handleGenerateCroppedUrls = async () => {
    const croppedUrls = await cropCalculation.generateCroppedUrls();
    
    // Apply rectangle overlays to cropped URLs
    const primaryOverlayUrl = generateUrlWithOverlays(croppedUrls.primaryCroppedUrl, 0);
    const secondaryOverlayUrl = generateUrlWithOverlays(croppedUrls.secondaryCroppedUrl, 1);
    
    
    // Update internal state and call callback  
    const urls = {
      primary: primaryOverlayUrl || undefined,
      secondary: secondaryOverlayUrl || undefined
    };
    setCropUrls(urls);
    if (onCropUrlsChange) {
      onCropUrlsChange(urls);
    }
    
    return {
      primaryCroppedUrl: primaryOverlayUrl,
      secondaryCroppedUrl: secondaryOverlayUrl
    };
  };

  const generateUrlWithOverlays = (baseUrl: string | null, imageIndex: number): string | null => {
    if (!baseUrl) return null;
    
    const rectangles = drawing.drawingState.rectangles[imageIndex] || [];
    if (rectangles.length === 0) return baseUrl;
    
    // Convert rectangles to Cloudinary overlay parameters
    const imageElement = document.querySelector(`img[alt="Asset preview ${imageIndex + 1}"]`) as HTMLImageElement;
    const containerElement = imageElement?.parentElement;
    
    if (!imageElement || !containerElement) return baseUrl;
    
    const overlayParams = rectangles.map(rect =>
      CloudinaryOverlays.convertDisplayToImageCoords(
        rect,
        imageElement,
        containerElement,
        imageInteraction.imageZooms[imageIndex] || 1,
        imageInteraction.imagePositions[imageIndex]?.x || 0,
        imageInteraction.imagePositions[imageIndex]?.y || 0
      )
    );
    
    return CloudinaryOverlays.generateMultipleOverlaysUrl(baseUrl, overlayParams);
  };

  // Handle view mode change with crop URL update
  const handleViewModeChange = React.useCallback(async (newViewMode: string) => {
    setViewMode(newViewMode);

    // Immediately update crop URLs for the new view mode
    setTimeout(async () => {
      const croppedUrls = await cropCalculation.generateCroppedUrls();
      const primaryOverlayUrl = generateUrlWithOverlays(croppedUrls.primaryCroppedUrl, 0);
      const secondaryOverlayUrl = generateUrlWithOverlays(croppedUrls.secondaryCroppedUrl, 1);

      const urls = {
        primary: primaryOverlayUrl || undefined,
        secondary: secondaryOverlayUrl || undefined
      };
      setCropUrls(urls);
      if (onCropUrlsChange) {
        onCropUrlsChange(urls);
      }
    }, 100); // Short delay to ensure view mode state is updated
  }, [cropCalculation, onCropUrlsChange]);

  const handleResetImage = (imageIndex: number) => {
    // Reset zoom to 100%
    imageInteraction.handleSetZoom(imageIndex, 100);
    // Reset position to 0,0 and clear rectangles
    imageInteraction.handleResetZoom(imageIndex);
    // Clear rectangles for this image
    drawing.clearRectangles(imageIndex);
  };

  const handleSelectRectangle = (imageId: string, rectIndex: number) => {
    drawing.selectRectangle(imageId, rectIndex);
  };

  const handleDeleteRectangle = () => {
    // Clear all rectangles for the selected image (like reset but without zoom/position changes)
    if (drawing.drawingState.selectedRectangle) {
      const imageId = drawing.drawingState.selectedRectangle.imageId;
      
      // Clear from drawing state
      drawing.clearRectangles(imageId, handleRectangleChange);
      
      // Also clear from the current rectangles state to trigger immediate UI update
      const clearedRectangles = {
        ...drawing.drawingState.rectangles,
        [imageId]: []
      };
      handleRectangleChange(clearedRectangles);
    }
  };

  const handleMouseLeave = () => {
    setHoveredImage(null);
    imageInteraction.setActiveDropdown(null);
    if (imageInteraction.dragState.isDragging) {
      imageInteraction.handleMouseUp();
    }
    if (drawing.drawingState.isDrawing) {
      setCurrentDrawing(null);
    }
  };

  const handleCroppedImageClick = async (imageIndex: number) => {
    try {
      const croppedUrl = await cropCalculation.generateCroppedUrl(imageIndex);
      if (croppedUrl) {
        window.open(croppedUrl, '_blank');
      } else {
        console.error('Failed to generate cropped URL');
      }
    } catch (error) {
      console.error('Error generating cropped URL:', error);
    }
  };

  // Apply theme styles
  const themeStyles = theme.colors ? {
  } as React.CSSProperties : {};

  return (
    <div
      className={`w-full h-full bg-background flex justify-center items-center p-4 ${className || ''}`}
      style={themeStyles}
    >
      {/* Asset Card Only */}
      <GridAssetPanel
        aspectRatio={aspectRatio}
        viewMode={viewMode}
        header={header}
        subheader={subheader}
        companyName={companyName}
        companyLogo={companyLogo}
        primaryImage={primaryImage}
        secondaryImage={secondaryImage}
        primaryImages={primaryImages}
        secondaryImages={secondaryImages}
        selectedPrimaryVersion={selectedPrimaryVersion}
        selectedSecondaryVersion={selectedSecondaryVersion}
        onPrimaryVersionChange={setSelectedPrimaryVersion}
        onSecondaryVersionChange={setSelectedSecondaryVersion}
        imagePositions={imageInteraction.imagePositions}
        imageZooms={imageInteraction.imageZooms}
        showHeader={true}
        activeTool={drawing.drawingState.activeTool}
        onToolChange={drawing.setActiveTool}
        rectangles={rectanglesForDebug}
        aspectRatios={aspectRatios}
        availableViewModes={availableViewModes}
        onAspectRatioChange={setAspectRatio}
        onViewModeChange={handleViewModeChange}
        onHeaderChange={setHeader}
        onSubheaderChange={setSubheader}
        showSaveControls={showSaveControls}
        onSave={handleSave}
        onCancel={onCancel}
        enableCollectionChange={enableCollectionChange}
        initialCollectionId={initialCollectionId}
        onCollectionChange={onCollectionChange}
        imageUrls={{
          primaryOriginal: primaryImage,
          primaryCropped: cropUrls.primary,
          primaryMarkup: initialData.customData?.primaryMarkupUrl || initialData.customData?.primary_markup_url,
          secondaryOriginal: secondaryImage,
          secondaryCropped: cropUrls.secondary,
          secondaryMarkup: initialData.customData?.secondaryMarkupUrl || initialData.customData?.secondary_markup_url
        }}
        onGridImageZoomChange={(zoomLevels) => {
          // Convert string keys to numeric keys for imageInteraction
          const numericZooms: Record<number, number> = {};

          // Map all possible image keys to their corresponding indices
          if (zoomLevels.primary1) numericZooms[0] = zoomLevels.primary1;
          if (zoomLevels.secondary1) numericZooms[1] = zoomLevels.secondary1;
          if (zoomLevels.primary2) numericZooms[0] = zoomLevels.primary2;   // primary2 also maps to index 0
          if (zoomLevels.secondary2) numericZooms[1] = zoomLevels.secondary2; // secondary2 also maps to index 1
          if (zoomLevels.primary3) numericZooms[0] = zoomLevels.primary3;   // primary3 also maps to index 0

          // Update both systems to keep them in sync
          setGridImageZooms(prev => ({ ...prev, ...zoomLevels }));
          Object.entries(numericZooms).forEach(([index, zoom]) => {
            imageInteraction.handleSetZoom(parseInt(index), Math.round(zoom * 100), true);
          });
        }}
        onGridImagePositionChange={(positions) => {
          // Convert string keys to numeric keys for imageInteraction
          const numericPositions: Record<number, { x: number; y: number }> = {};

          // Map all possible image keys to their corresponding indices
          if (positions.primary1) numericPositions[0] = positions.primary1;
          if (positions.secondary1) numericPositions[1] = positions.secondary1;
          if (positions.primary2) numericPositions[0] = positions.primary2;   // primary2 also maps to index 0
          if (positions.secondary2) numericPositions[1] = positions.secondary2; // secondary2 also maps to index 1
          if (positions.primary3) numericPositions[0] = positions.primary3;   // primary3 also maps to index 0

          // Update both systems to keep them in sync
          setGridImagePositions(prev => ({ ...prev, ...positions }));
          Object.entries(numericPositions).forEach(([index, position]) => {
            imageInteraction.handleSetPosition(parseInt(index), position.x, position.y);
          });
        }}
        onRectangleChange={(rectangles) => {
          // Use proper state setter instead of direct mutation
          drawing.setRectangles(rectangles);
        }}
        selectedRectangle={drawing.drawingState.selectedRectangle}
        onSelectRectangle={handleSelectRectangle}
        onDeleteRectangle={handleDeleteRectangle}
        onResetImage={(imageId) => {
          // Convert imageId to imageIndex for the reset handler
          const imageIndex = imageId.includes('primary') ? 0 : 1;
          handleResetImage(imageIndex);
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={imageInteraction.handleMouseLeave}
        isLinked={isLinked}
        onLinkStateChange={setIsLinked}
        />
    </div>
  );
};